/**
 * TypeScript implementation of the Python KnowledgeManager class
 * Enhanced with advanced analytics and visualization support
 */

// Define types for better type safety
export interface ContentItem {
  title: string;
  url: string;
  snippet: string;
  query: string;
  page: number;
  date: string;
  contentType?: string;
  confidence?: number;
  domain?: string;
  discovered_date: string;
  risk_score?: number;
  dmca_status?: string;
  last_checked?: string;
}

export interface MasterData {
  [creatorName: string]: ContentItem[];
}

export interface ContentStats {
  total_urls: number;
  domains: Record<string, number>;
  content_types: Record<string, number>;
  newest_content: string | null;
  oldest_content: string | null;
  confidence_levels: Record<string, number>;
  risk_assessment: {
    high_risk: number;
    medium_risk: number;
    low_risk: number;
  };
  discovery_timeline: Record<string, number>;
}

export class KnowledgeManager {
  private masterData: MasterData = {};
  private readonly storageKey = 'contentLeakMasterData';
  
  constructor() {
    // Initialize master data store from localStorage
    this.loadMasterData();
  }
  
  /**
   * Load master data from storage
   */
  private loadMasterData(): void {
    try {
      const storedData = localStorage.getItem(this.storageKey);
      if (storedData) {
        this.masterData = JSON.parse(storedData);
        console.log(`📊 Loaded master data with ${Object.keys(this.masterData).length} creators`);
        
        // Log the total number of content items
        const totalItems = Object.values(this.masterData).reduce(
          (total, items) => total + items.length, 0
        );
        console.log(`📋 Total content items: ${totalItems}`);
      }
    } catch (e) {
      console.error('Failed to load master data:', e);
    }
  }
  
  /**
   * Save master data to storage
   */
  private saveMasterData(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.masterData));
    } catch (e) {
      console.error('Failed to save master data:', e);
      
      // If the error is due to quota exceeded, try to save a compressed version
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        this.saveCompressedMasterData();
      }
    }
  }
  
  /**
   * Save a compressed version of the master data to handle storage limitations
   */
  private saveCompressedMasterData(): void {
    try {
      // Create a simplified version with only essential fields
      const compressedData: Record<string, any[]> = {};
      
      for (const [creator, items] of Object.entries(this.masterData)) {
        compressedData[creator] = items.map(item => ({
          url: item.url,
          title: item.title.substring(0, 100), // Truncate long titles
          snippet: item.snippet.substring(0, 150), // Truncate long snippets
          contentType: item.contentType,
          discovered_date: item.discovered_date
        }));
      }
      
      localStorage.setItem(`${this.storageKey}_compressed`, JSON.stringify(compressedData));
      console.log('Saved compressed master data due to storage limitations');
    } catch (e) {
      console.error('Failed to save compressed master data:', e);
    }
  }
  
  /**
   * Update master content repository with new results
   */
  public updateMasterContent(newResults: any[], creatorName: string): number {
    if (!newResults || newResults.length === 0) {
      console.log("❌ No new results to add");
      return 0;
    }
    
    const normalizedName = creatorName.replace(/\s+/g, '_');
    
    // Initialize master data for this creator if it doesn't exist
    if (!this.masterData[normalizedName]) {
      this.masterData[normalizedName] = [];
    }
    
    // Process new records with enhanced metadata
    const resultsWithMetadata = newResults.map(result => {
      // Extract domain from URL
      let domain = '';
      try {
        domain = result.url.split('//')[1].split('/')[0];
      } catch {
        // Skip if we can't parse the domain
      }
      
      // Calculate risk score if not present
      const riskScore = result.risk_score || this.calculateRiskScore(result, domain);
      
      return {
        ...result,
        discovered_date: result.discovered_date || new Date().toISOString().split('T')[0],
        domain: result.domain || domain,
        contentType: result.contentType || this.detectContentType(result.url),
        confidence: result.confidence || 0.5,
        risk_score: riskScore,
        dmca_status: 'pending',
        last_checked: new Date().toISOString().split('T')[0]
      };
    });
    
    // Check for duplicates based on URL
    const existingUrls = new Set(this.masterData[normalizedName].map(item => item.url));
    const uniqueResults = resultsWithMetadata.filter(result => !existingUrls.has(result.url));
    
    // Append to master data
    if (uniqueResults.length > 0) {
      this.masterData[normalizedName].push(...uniqueResults);
      this.saveMasterData();
      console.log(`✅ Added ${uniqueResults.length} new records to master content`);
      return uniqueResults.length;
    } else {
      console.log("ℹ️ No new content to add to master repository");
      return 0;
    }
  }
  
  /**
   * Calculate risk score based on content properties
   */
  private calculateRiskScore(item: any, domain: string): number {
    let score = 0.5; // Base score
    
    // Higher risk for certain domains
    const highRiskDomains = ['onlyfans.com', 'patreon.com', 'fancentro.com'];
    const mediumRiskDomains = ['reddit.com', 'imgur.com', 'discord.com'];
    
    if (highRiskDomains.some(d => domain.includes(d))) {
      score += 0.3;
    } else if (mediumRiskDomains.some(d => domain.includes(d))) {
      score += 0.2;
    }
    
    // Higher risk for certain content types
    if (item.contentType === 'video') {
      score += 0.2;
    } else if (item.contentType === 'image') {
      score += 0.1;
    }
    
    // Higher risk for high confidence matches
    if (item.confidence && item.confidence > 0.7) {
      score += 0.2;
    }
    
    // Limit score to 0-1 range
    return Math.min(1, Math.max(0, score));
  }
  
  /**
   * Detect content type based on URL patterns
   */
  private detectContentType(url: string): string {
    const videoDomains = ['youtube.com', 'vimeo.com', 'tiktok.com', 'twitch.tv'];
    const imageDomains = ['instagram.com', 'imgur.com', 'flickr.com', 'pinterest.com'];
    const documentDomains = ['docs.google.com', 'dropbox.com', 'scribd.com'];
    
    const domain = url.split('//')[1]?.split('/')[0] || '';
    
    if (videoDomains.some(d => domain.includes(d)) || url.includes('/video/') || url.includes('watch?v=')) {
      return 'video';
    } else if (imageDomains.some(d => domain.includes(d)) || url.match(/\.(jpg|jpeg|png|gif|webp)($|\?)/i)) {
      return 'image';
    } else if (documentDomains.some(d => domain.includes(d)) || url.match(/\.(pdf|doc|docx|ppt|pptx|xls|xlsx)($|\?)/i)) {
      return 'document';
    } else {
      return 'website';
    }
  }
  
  /**
   * Get statistics about collected content
   */
  public getContentStats(creatorName: string): ContentStats {
    const normalizedName = creatorName.replace(/\s+/g, '_');
    
    if (!this.masterData[normalizedName] || this.masterData[normalizedName].length === 0) {
      return {
        total_urls: 0,
        domains: {},
        content_types: {},
        newest_content: null,
        oldest_content: null,
        confidence_levels: {
          high: 0,
          medium: 0,
          low: 0
        },
        risk_assessment: {
          high_risk: 0,
          medium_risk: 0,
          low_risk: 0
        },
        discovery_timeline: {}
      };
    }
    
    const data = this.masterData[normalizedName];
    
    // Extract domains
    const domains: Record<string, number> = {};
    const contentTypes: Record<string, number> = {};
    const confidenceLevels: Record<string, number> = {
      high: 0,
      medium: 0,
      low: 0
    };
    const riskAssessment = {
      high_risk: 0,
      medium_risk: 0,
      low_risk: 0
    };
    const discoveryTimeline: Record<string, number> = {};
    
    for (const item of data) {
      // Process domains
      const domain = item.domain || '';
      if (domain) {
        domains[domain] = (domains[domain] || 0) + 1;
      }
      
      // Process content types
      const contentType = item.contentType || 'unknown';
      contentTypes[contentType] = (contentTypes[contentType] || 0) + 1;
      
      // Process confidence levels
      const confidence = item.confidence || 0.5;
      if (confidence >= 0.7) {
        confidenceLevels.high++;
      } else if (confidence >= 0.4) {
        confidenceLevels.medium++;
      } else {
        confidenceLevels.low++;
      }
      
      // Process risk assessment
      const riskScore = item.risk_score || 0.5;
      if (riskScore >= 0.7) {
        riskAssessment.high_risk++;
      } else if (riskScore >= 0.4) {
        riskAssessment.medium_risk++;
      } else {
        riskAssessment.low_risk++;
      }
      
      // Process discovery timeline
      const discoveryDate = item.discovered_date || '';
      if (discoveryDate) {
        discoveryTimeline[discoveryDate] = (discoveryTimeline[discoveryDate] || 0) + 1;
      }
    }
    
    // Get date ranges
    const dates = data
      .filter(item => item.discovered_date)
      .map(item => item.discovered_date);
    
    const dateStats = {
      newest_content: dates.length > 0 ? dates.sort().reverse()[0] : null,
      oldest_content: dates.length > 0 ? dates.sort()[0] : null
    };
    
    return {
      total_urls: data.length,
      domains,
      content_types: contentTypes,
      newest_content: dateStats.newest_content,
      oldest_content: dateStats.oldest_content,
      confidence_levels: confidenceLevels,
      risk_assessment: riskAssessment,
      discovery_timeline: discoveryTimeline
    };
  }
  
  /**
   * Export master data in different formats
   */
  public exportMasterData(creatorName: string, format: string = 'json'): any {
    const normalizedName = creatorName.replace(/\s+/g, '_');
    
    if (!this.masterData[normalizedName] || this.masterData[normalizedName].length === 0) {
      console.log("❌ No master data found for export");
      return null;
    }
    
    const data = this.masterData[normalizedName];
    
    if (format.toLowerCase() === 'json') {
      return data;
    } else if (format.toLowerCase() === 'csv') {
      return this.convertToCSV(data);
    } else if (format.toLowerCase() === 'excel') {
      // For Excel, we return CSV format which can be easily imported into Excel
      return this.convertToCSV(data);
    } else if (format.toLowerCase() === 'report') {
      // Generate a summary report
      return this.generateReport(creatorName);
    } else {
      console.log(`❌ Unsupported export format: ${format}`);
      return null;
    }
  }
  
  /**
   * Convert data to CSV format
   */
  private convertToCSV(data: any[]): string {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows: string[] = [];
    
    // Add header row
    csvRows.push(headers.join(','));
    
    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        // Handle values with commas or quotes
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  }
  
  /**
   * Generate a detailed report for a creator
   */
  private generateReport(creatorName: string): any {
    const stats = this.getContentStats(creatorName);
    const normalizedName = creatorName.replace(/\s+/g, '_');
    const data = this.masterData[normalizedName] || [];
    
    // Sort domains by count
    const topDomains = Object.entries(stats.domains)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([domain, count]) => ({ domain, count }));
    
    // Get high-risk content
    const highRiskContent = data
      .filter(item => (item.risk_score || 0) >= 0.7)
      .slice(0, 10);
    
    // Get recent discoveries
    const recentDiscoveries = [...data]
      .sort((a, b) => {
        const dateA = a.discovered_date || '';
        const dateB = b.discovered_date || '';
        return dateB.localeCompare(dateA);
      })
      .slice(0, 10);
    
    return {
      creator: creatorName,
      generated_at: new Date().toISOString(),
      summary: {
        total_content: stats.total_urls,
        domains_count: Object.keys(stats.domains).length,
        content_types: stats.content_types,
        risk_assessment: stats.risk_assessment,
        date_range: {
          first_discovered: stats.oldest_content,
          latest_discovered: stats.newest_content
        }
      },
      top_domains: topDomains,
      high_risk_content: highRiskContent,
      recent_discoveries: recentDiscoveries
    };
  }
  
  /**
   * Get visualization data for charts and graphs
   */
  public getVisualizationData(creatorName: string): any {
    const stats = this.getContentStats(creatorName);
    
    // Domain distribution for pie chart
    const domainDistribution = Object.entries(stats.domains)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([domain, count], index) => ({
        id: domain,
        label: domain,
        value: count,
        color: this.getColorForIndex(index)
      }));
    
    // Content type distribution for pie chart
    const contentTypeDistribution = Object.entries(stats.content_types)
      .map(([type, count], index) => ({
        id: type,
        label: type.charAt(0).toUpperCase() + type.slice(1),
        value: count,
        color: this.getColorForContentType(type)
      }));
    
    // Risk assessment for pie chart
    const riskDistribution = [
      { id: 'high', label: 'High Risk', value: stats.risk_assessment.high_risk, color: '#FF6B6B' },
      { id: 'medium', label: 'Medium Risk', value: stats.risk_assessment.medium_risk, color: '#FFE66D' },
      { id: 'low', label: 'Low Risk', value: stats.risk_assessment.low_risk, color: '#4ECDC4' }
    ];
    
    // Discovery timeline for line chart
    const timelineData = this.processTimelineData(stats.discovery_timeline);
    
    return {
      domainDistribution,
      contentTypeDistribution,
      riskDistribution,
      discoveryTimeline: [{ id: 'discoveries', data: timelineData }]
    };
  }
  
  /**
   * Process timeline data for visualization
   */
  private processTimelineData(timelineData: Record<string, number>): any[] {
    // Sort dates
    const sortedDates = Object.keys(timelineData).sort();
    
    // If we have no data, return empty array
    if (sortedDates.length === 0) {
      return [];
    }
    
    // Fill in missing dates
    const result: any[] = [];
    const startDate = new Date(sortedDates[0]);
    const endDate = new Date(sortedDates[sortedDates.length - 1]);
    
    // If start and end are the same, just return that point
    if (startDate.getTime() === endDate.getTime()) {
      return [{ 
        x: sortedDates[0],
        y: timelineData[sortedDates[0]]
      }];
    }
    
    // Fill in all dates between start and end
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      result.push({
        x: dateStr,
        y: timelineData[dateStr] || 0
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return result;
  }
  
  /**
   * Get a color for a domain based on index
   */
  private getColorForIndex(index: number): string {
    const colors = [
      '#3498db', // Blue
      '#e74c3c', // Red
      '#2ecc71', // Green
      '#f39c12', // Orange
      '#9b59b6', // Purple
      '#1abc9c', // Turquoise
      '#d35400', // Pumpkin
      '#34495e', // Dark Blue
      '#7f8c8d', // Gray
      '#2c3e50'  // Navy
    ];
    
    return colors[index % colors.length];
  }
  
  /**
   * Get a color based on content type
   */
  private getColorForContentType(type: string): string {
    const colorMap: Record<string, string> = {
      'video': '#FF6B6B',    // Red
      'image': '#4ECDC4',    // Teal
      'document': '#FFE66D', // Yellow
      'website': '#1A535C',  // Dark Teal
      'social': '#F7FFF7',   // Light Green
      'unknown': '#7F8C8D'   // Gray
    };
    
    return colorMap[type] || '#7F8C8D';
  }
}
